import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect } from "react";
import { LogOut } from "lucide-react";

export default function EnhancedAuthLogout({ className = "" }) {
  const { logout } = useAuth0();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  const handleLogout = async () => {
    setIsLoggingOut(true);
    
    try {
      // 1. Clear all client-side storage
      localStorage.clear();
      sessionStorage.clear();
      
      // 2. Clear all cookies (including SameSite and HttpOnly when possible)
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;";
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname + ";";
      }
      
      // 3. Try to bypass cache by adding timestamp to logout URL
      const timestamp = new Date().getTime();
      const returnToUrl = `${window.location.origin}?nocache=${timestamp}`;
      
      // 4. Use direct URL method to force a new auth flow
      const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || "dev-d1aq0xw72d714fi0.us.auth0.com";
      const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || "m1SH9jfmUfiUoLrvtrb9M7YdVQ5Tsx1c";
      
      // Direct logout URL with federated parameter and cache busting
      const logoutUrl = `https://${auth0Domain}/v2/logout?client_id=${clientId}&returnTo=${encodeURIComponent(returnToUrl)}&federated=true&_=${timestamp}`;
      
      console.log("Executing hard logout redirect to:", logoutUrl);
      
      // 5. We're bypassing Auth0's SDK logout and forcing a direct redirect
      // This ensures a complete session reset on Auth0's side
      window.location.href = logoutUrl;
      
      // The page will redirect, so any code after this likely won't execute
    } catch (error) {
      console.error("Enhanced logout failed:", error);
      setIsLoggingOut(false);
      
      // Fallback to standard Auth0 logout as a last resort
      logout({
        logoutParams: {
          returnTo: window.location.origin,
          federated: true
        }
      });
    }
  };
  
  // Reset any Auth0 iframe attempts
  useEffect(() => {
    // Set interval to periodically check for and remove Auth0 iframes
    const intervalId = setInterval(() => {
      const iframes = document.querySelectorAll('iframe[src*="auth0"]');
      iframes.forEach(iframe => {
        if (iframe && iframe.parentNode) {
          console.log("Removing Auth0 iframe:", iframe.src);
          iframe.parentNode.removeChild(iframe);
        }
      });
    }, 500);
    
    return () => clearInterval(intervalId);
  }, []);
  
  return (
    <button
      onClick={handleLogout}
      disabled={isLoggingOut}
      className={`flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all duration-200
        ${isLoggingOut 
          ? "bg-gray-200 text-gray-500 cursor-not-allowed" 
          : "bg-white border border-red-500 text-red-500 hover:bg-red-50 hover:shadow-md"}
        ${className}`}
    >
      {isLoggingOut ? (
        <>
          <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Signing out...
        </>
      ) : (
        <>
          <LogOut className="h-4 w-4" />
          Sign Out
        </>
      )}
    </button>
  );
}